/**
 * Component to add and select a payment method during donation flow, along with
 * prompts to encourage giving using bank account.
 *
 * Shows the default selected payment method, if any, and allows the user to
 * change it.
 */

import { ButtonTargetKind, Button, ButtonRole } from "@components/Button";
import { IconSize, IconDisplay } from "@components/Icon";
import { TextInputLabelText } from "@components/InputContainer";
import { Loading, LoadingIcon } from "@components/LoadingIndicator";
import { COLLECT_ZIP } from "@components/donate/DonateV3/PaymentProcess/components/AddCreditCard";
import {
  PaymentsContainer,
  PaymentMethodList,
  AddCardPaymentItem,
  AddBankPaymentItem,
  PaymentMethodItem,
  AddNewCardPaymentWidget,
  bankPlaceholderItem,
} from "@components/donate/DonateV3/PaymentProcess/components/PaymentMethod";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import {
  DonateFormType,
  DonateModalAction,
} from "@components/donate/DonateV3/types";
import { Big } from "big.js";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { UseFormSetError } from "react-hook-form";

import {
  MAX_NUM_PAYMENT_METHODS,
  NonprofitResponse,
  PaymentSourceResponse as PaymentSource,
  StripePaymentSourceResponse as StripePaymentSource,
} from "@every.org/common/src/codecs/entities";
import { Location } from "@every.org/common/src/codecs/location";
import { coerceToSafeIntOrThrow } from "@every.org/common/src/codecs/number";
import {
  Currency,
  DonationFrequency,
  PaymentSourceType,
} from "@every.org/common/src/entity/types";
import { currencyValueToMinimumDenominationAmount } from "@every.org/common/src/helpers/currency";

import { trackEvent } from "src/utility/analytics";

interface PaymentSourceListProps {
  className?: string;
  /**
   * Giving credit to consume, which will be listed as a payment source.
   */
  creditAmount: Big;

  /**
   * Amount to charge on the selected payment source.
   */
  amountToCharge: Big;

  /**
   * Base donation amount, before tip/credit - passed to AddCreditCard
   * alongside tipAmount/creditAmount so the backend can compute the same
   * paidAmount /donate itself will charge.
   */
  amountBig: Big;

  /**
   * Donation tip to every.org - passed separately (not netted into
   * amountBig) to the PaymentIntent.
   */
  tipAmount: Big;

  creditsOnly: boolean;

  donateAction?: DonateModalAction;

  selectedPaymentSourceType: PaymentSourceType;

  /**
   * Whether a payment source such as a card or bank account is needed. If true,
   * renders the selected payment source or the options to edit/add a new source.
   */
  renewablePaymentSourceRequired: boolean;

  paymentSources?: DonateFormContext["paymentSources"];
  setPaymentSources: DonateFormContext["setPaymentSources"];

  selectedPaymentSource: PaymentSource | undefined;
  setSelectedPaymentSource: (paymentSource?: PaymentSource) => void;
  setErrorMessage: (error?: string) => void;
  label?: string;
  disallowAddingType?: Set<StripePaymentSource["type"]>;
  /**
   * Hide the helper text and do not allow PayPal for gift card purchases.
   */
  isGiftCardPurchase?: boolean;
  onAddingCard?: (isAddingCard: boolean) => void;
  firstName?: string;
  lastName?: string;
  email?: string;
  clearFormError?: () => void;
  setFormError?: UseFormSetError<DonateFormType>;
  nonprofit?: NonprofitResponse;
  frequency?: DonationFrequency;
  /**
   * mode: "payment" only: forwarded to AddCreditCard, which registers a
   * function on it to create and confirm a PaymentIntent for the entered
   * card on demand - see AddCreditCardProps.createNewCardPaymentIntentRef.
   */
  createNewCardPaymentIntentRef?: React.MutableRefObject<
    | (() => Promise<{ paymentMethodId: string; paymentIntentId: string }>)
    | undefined
  >;
  /**
   * Forwarded to AddCreditCard - see AddCreditCardProps.location and
   * .isAddressRequired.
   */
  location?: Location;
  isAddressRequired?: boolean;
}

export const SelectPaymentSource: React.FCC<PaymentSourceListProps> = (
  props
) => {
  const {
    paymentSources,
    setPaymentSources,
    selectedPaymentSource,
    selectedPaymentSourceType,
    setSelectedPaymentSource,
    setErrorMessage,
    creditsOnly,
    donateAction,
    renewablePaymentSourceRequired,
    className,
    label,
    onAddingCard,
    firstName,
    lastName,
    email,
    clearFormError,
    setFormError,
    nonprofit,
    frequency,
    amountToCharge,
    amountBig,
    tipAmount,
    creditAmount,
    createNewCardPaymentIntentRef,
    location,
    isAddressRequired,
  } = props;

  // The donate flow uses the payment-intent flow for card entry whenever
  // there's something to charge - recurring's mandatory save is handled by
  // setup_future_usage: "off_session" being forced server-side
  // (createCardPaymentIntent), and a one-time donor's opt-in save is
  // driven by a checkbox in AddCreditCard, not by which mode renders.
  // When credits fully cover the charge (a recurring donation adding a
  // backup payment method), there's no amount to build a PaymentIntent
  // around (Stripe rejects zero-amount intents), so fall back to the same
  // SetupIntent-based "add card" flow the "My Giving" payment methods page
  // (PaymentPage.tsx) uses: the card is added and saved via its own
  // "Add card" button, auto-selected, and then rides along on /donate as a
  // saved source with no charge.
  // Updating an existing donation (e.g. retrying after a failed charge) is
  // the same either way: submitDonation's UPDATE branch always needs an
  // already-attached PaymentSource (it references it by externalId) and
  // never calls createNewCardPaymentIntentRef, so a new card must go
  // through the SetupIntent/"Add card" flow here too, regardless of
  // amountToCharge.
  const mode =
    amountToCharge.gt(0) && donateAction !== DonateModalAction.UPDATE
      ? ("payment" as const)
      : ("setup" as const);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newBankAccountDetailsLoading, setNewBankAccountDetailsLoading] =
    useState(false);

  const [addBankAccountTriggered, setAddBankAccountTriggered] = useState(false);
  const [addBankAccountBtn, setAddBankAccountBtn] =
    useState<HTMLElement | null>(null);
  const [oldPaymentSource, setOldPaymentSource] = useState(
    selectedPaymentSource
  );

  const disallowAddingType = props.disallowAddingType ?? new Set();

  // remove old payment source if payment sources are empty
  // this is happening for logged out users
  // when paymentSources resets to empty array on user info change
  useEffect(() => {
    if (paymentSources?.length === 0) {
      setOldPaymentSource(undefined);
    }
  }, [paymentSources]);

  // if add bank account is requested but button to add bank account is still
  // being mounted, this waits for it to mount and then clicks the button for
  // you.
  useEffect(
    function triggerAddBankAccount() {
      if (addBankAccountTriggered && !!addBankAccountBtn) {
        setAddBankAccountTriggered(false);
        addBankAccountBtn.click();
      }
    },
    [addBankAccountTriggered, addBankAccountBtn]
  );

  const onPaymentSourceAdded = useCallback(
    (paymentSource: PaymentSource) => {
      setNewBankAccountDetailsLoading(false); // even if wasn't bank acct, this is safe
      setPaymentSources((prevSources) =>
        (prevSources?.filter((s) => s.id !== paymentSource.id) || []).concat(
          paymentSource
        )
      );
      setSelectedPaymentSource(paymentSource);
      setErrorMessage(undefined);
      clearFormError && clearFormError();
      setIsAddingCard(false);
      trackEvent("Payment source added", { type: paymentSource.type });
    },
    [
      setPaymentSources,
      setSelectedPaymentSource,
      setErrorMessage,
      clearFormError,
    ]
  );

  const onAddBankAccountFailure = useCallback(
    (errorMessage: string) => {
      setNewBankAccountDetailsLoading(false); // even if wasn't bank acct, this is safe
      setErrorMessage(errorMessage);
    },
    [setErrorMessage]
  );

  const onPlaidConnectionSuccess = useCallback(() => {
    setNewBankAccountDetailsLoading(true);
  }, []);

  const availablePaymentSources = useMemo(
    () =>
      paymentSources &&
      paymentSources.filter(
        (source) => source.type === selectedPaymentSourceType
      ),

    [paymentSources, selectedPaymentSourceType]
  );

  /**
   * If the currently selected payment source is not in the available list of
   * payment sources, pick either the default or the first payment source from
   * actually available payment sources
   */
  useEffect(() => {
    if (
      availablePaymentSources &&
      selectedPaymentSource &&
      !availablePaymentSources?.find(
        (source) => source.id === selectedPaymentSource.id
      )
    ) {
      setSelectedPaymentSource(
        availablePaymentSources.find((source) => source.default) ||
          availablePaymentSources[0]
      );
    }
  }, [
    availablePaymentSources,
    selectedPaymentSource,
    setSelectedPaymentSource,
  ]);

  const showAddCard =
    isAddingCard ||
    // Always show add card if no payment sources and type is cc
    (selectedPaymentSourceType === PaymentSourceType.CARD &&
      availablePaymentSources?.length === 0 &&
      !creditsOnly);

  // Covers both the explicit "Add card" click (isAddingCard) and the
  // default no-saved-sources case above, so callers can tell whenever the
  // add-card element is actually on screen - not just when the user
  // deliberately opened it.
  useEffect(() => {
    onAddingCard && onAddingCard(showAddCard);
  }, [showAddCard, onAddingCard]);

  const collectZip = useMemo(() => {
    return nonprofit?.metadata?.collectCardZip
      ? COLLECT_ZIP.OPTIONAL
      : COLLECT_ZIP.NO;
  }, [nonprofit]);

  if (availablePaymentSources === undefined) {
    return <Loading />;
  }

  if (showAddCard) {
    const amountToChargeMinDenom = currencyValueToMinimumDenominationAmount({
      value: { amount: amountToCharge, currency: Currency.USD },
    });
    return (
      <PaymentsContainer className={className}>
        <AddNewCardPaymentWidget
          handleConnectBankAccount={() => {
            setIsAddingCard(false);
            setAddBankAccountTriggered(true);
          }}
          onAddCancelled={() => {
            setIsAddingCard(false);
            setSelectedPaymentSource(oldPaymentSource);
          }}
          // Only allow cancelling back to the picker if there's something to
          // pick from.
          cancellable={availablePaymentSources.length > 0}
          onPaymentSourceAdded={onPaymentSourceAdded}
          firstName={firstName}
          lastName={lastName}
          email={email}
          isAddingCard={isAddingCard}
          collectZip={collectZip}
          setFormError={setFormError}
          clearFormError={clearFormError}
          mode={mode}
          amountInMinDenom={
            mode === "payment" ? amountToChargeMinDenom : undefined
          }
          toNonprofitId={mode === "payment" ? nonprofit?.id : undefined}
          value={
            mode === "payment"
              ? { amount: amountBig, currency: Currency.USD }
              : undefined
          }
          frequency={mode === "payment" ? frequency : undefined}
          tipAmount={
            mode === "payment"
              ? coerceToSafeIntOrThrow({
                  num: currencyValueToMinimumDenominationAmount({
                    value: { amount: tipAmount, currency: Currency.USD },
                  }),
                })
              : undefined
          }
          creditAmount={
            mode === "payment"
              ? coerceToSafeIntOrThrow({
                  num: currencyValueToMinimumDenominationAmount({
                    value: { amount: creditAmount, currency: Currency.USD },
                  }),
                })
              : undefined
          }
          createNewCardPaymentIntentRef={
            mode === "payment" ? createNewCardPaymentIntentRef : undefined
          }
          location={location}
          isAddressRequired={isAddressRequired}
        />
      </PaymentsContainer>
    );
  }

  const paymentListItems: { elem: React.ReactNode; key: string }[] = [];

  if (renewablePaymentSourceRequired) {
    if (selectedPaymentSource) {
      const onClick = () => {
        setOldPaymentSource(selectedPaymentSource);
        setSelectedPaymentSource(undefined);
      };
      const key =
        selectedPaymentSource?.id || selectedPaymentSource.paymentMethod;
      paymentListItems.push({
        elem: (
          <PaymentMethodItem
            source={selectedPaymentSource}
            onClick={onClick}
            tabIndex={-1}
            end={
              <Button
                data-tname="changePaymentMethodButton"
                role={ButtonRole.TEXT_ONLY}
                onClick={{ kind: ButtonTargetKind.FUNCTION, action: onClick }}
              >
                Change
              </Button>
            }
          />
        ),
        key,
      });
    } else {
      const paymentSourcesToDisplay = newBankAccountDetailsLoading
        ? [bankPlaceholderItem, ...availablePaymentSources]
        : availablePaymentSources;
      paymentSourcesToDisplay.forEach((source) => {
        const isBankPlaceholderItem = source.id === bankPlaceholderItem.id;
        const key = source.id || source.paymentMethod;
        paymentListItems.push({
          elem: (
            <PaymentMethodItem
              source={source}
              onClick={() => {
                setSelectedPaymentSource(source);
                setErrorMessage(undefined);
                clearFormError && clearFormError();
              }}
              // if being deleted, dont allow selecting; if new bank account
              // loading, it will be auto-selected so dont let anything get
              // selected
              disabled={
                // !!deletingPaymentMethod ||
                newBankAccountDetailsLoading
              }
              end={
                isBankPlaceholderItem ? (
                  <LoadingIcon
                    size={IconSize.X_SMALL}
                    display={IconDisplay.SECONDARY}
                  />
                ) : undefined
              }
            />
          ),
          key,
        });
      });
    }
  }

  const canAddSources =
    !selectedPaymentSource &&
    availablePaymentSources.length < MAX_NUM_PAYMENT_METHODS &&
    !creditsOnly;
  const canAddCard =
    canAddSources &&
    !disallowAddingType.has(PaymentSourceType.CARD) &&
    selectedPaymentSourceType === PaymentSourceType.CARD;
  const canAddBank =
    canAddSources &&
    !disallowAddingType.has(PaymentSourceType.ACH_DEBIT) &&
    selectedPaymentSourceType === PaymentSourceType.ACH_DEBIT &&
    !newBankAccountDetailsLoading;

  if (
    (paymentListItems.length === 0 && !canAddCard && !canAddBank) ||
    creditsOnly
  ) {
    return null;
  }

  return (
    <PaymentsContainer className={className}>
      {label && <TextInputLabelText>{label}</TextInputLabelText>}
      <PaymentMethodList>
        {paymentListItems.map(({ elem, key }) => (
          <li key={key}>{elem}</li>
        ))}
        {canAddCard && (
          <li key="addCard">
            <AddCardPaymentItem
              onClick={() => {
                setIsAddingCard(true);
              }}
            />
          </li>
        )}
        {canAddBank && (
          <li key="addBank">
            <AddBankPaymentItem
              ref={setAddBankAccountBtn}
              onPlaidConnectionSuccess={onPlaidConnectionSuccess}
              onCompletedAddingAccount={(result) => {
                if (result.success) {
                  onPaymentSourceAdded(result.paymentSource);
                } else {
                  onAddBankAccountFailure(
                    "Could not add bank account, please try again"
                  );
                }
              }}
              onExit={() => {
                // setIsAddingNewPayment(false);
              }}
              firstName={firstName}
              lastName={lastName}
              email={email}
              setFormError={setFormError}
            />
          </li>
        )}
      </PaymentMethodList>
    </PaymentsContainer>
  );
};
